import React, { useEffect, useState } from "react";
import {
  apiGet, apiPost, apiPatch,
  getStatHolidays, saveStatHolidayOverride, revertStatHoliday,
} from "../api";
import { useNavigate } from "react-router-dom";
import RollForwardSettings from "../components/rollforward/RollForwardSettings";
import CollapsibleSection from "../components/CollapsibleSection";
import InvoiceDescriptionsSection from "./settings/InvoiceDescriptionsSection";
import MasterFileSection from "./settings/MasterFileSection";
import { canManage } from "../utils/roles";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function AdminSettingsPage({ user }) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [timeCodes, setTimeCodes] = useState([]);
  const [fiscalYearEndMonth, setFiscalYearEndMonth] = useState(null);
  // The text size invoices print at, firm-wide. Held as a string while being
  // typed so a half-typed "1" does not momentarily save a 1pt invoice.
  const [invoiceFontSize, setInvoiceFontSize] = useState("");
  const [fontSizeError, setFontSizeError] = useState("");
  const [newEmployee, setNewEmployee] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    role: "STAFF",
    is_active: true,
    can_approve_time: false,
    can_finalize_invoices: false,
  });
  const [newRates, setNewRates] = useState({});
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [qbo, setQbo] = useState(null);
  const [activeTab, setActiveTab] = useState("general");

  // ===== Helpers: list <-> map for rates =====
  const ratesListToMap = (ratesList) => {
    const map = {};
    (ratesList || []).forEach((r) => {
      if (r && typeof r === "object" && "time_code" in r) {
        map[r.time_code] = String(r.hourly_rate ?? "");
      }
    });
    return map;
  };

  const ratesMapToList = (ratesMap) => {
    const out = [];
    Object.entries(ratesMap || {}).forEach(([code, val]) => {
      if (code) {
        out.push({
          time_code: code,
          hourly_rate: val === "" || val == null ? 0 : Number(val),
        });
      }
    });
    return out;
  };

  const displayName = (emp) => {
    const joined = `${(emp?.first_name || "").trim()} ${(emp?.last_name || "").trim()}`.trim();
    return emp?.full_name || joined || emp?.username || "Unknown";
  };

  useEffect(() => {
    if (!user || !canManage(user)) {
      navigate("/");
    }
  }, [user, navigate]);

  // ===== Load Data =====
  const loadAll = async () => {
    try {
      setLoading(true);
      // firm-settings is fetched non-fatally: a failure there must not abort
      // the employees/time-codes load (Promise.all rejects on the first
      // failure) and take down the whole employee section for an unrelated,
      // less important fetch. Mirrors the same isolation in InvoicesPage.
      const [emp, codes, firm] = await Promise.all([
        apiGet("/employees/"),
        apiGet("/time-codes/"),
        apiGet("/api/firm-settings/").catch(() => null),
      ]);
      setFiscalYearEndMonth(firm?.fiscal_year_end_month ?? null);
      setInvoiceFontSize(String(firm?.invoice_font_size ?? 13));

      // Normalize each employee's rates to a map for the UI
      const normalized = (Array.isArray(emp) ? emp : []).map((e) => ({
        ...e,
        rates: ratesListToMap(e.rates),
      }));

      setEmployees(normalized);
      setTimeCodes(Array.isArray(codes) ? codes : []);
    } catch (e) {
      console.error(e);
      setErr("Failed to load settings data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && canManage(user)) loadAll();
  }, [user]);

  // ===== Load QuickBooks connection status =====
  useEffect(() => {
    apiGet("/api/quickbooks/status/").then(setQbo).catch(() => setQbo(null));
  }, []);

  // ===== Save Fiscal Year End =====
  const saveFiscalYearEndMonth = async (month) => {
    // Belt and braces: the <select> is disabled while fiscalYearEndMonth is
    // null (the initial load hasn't produced a real value yet), but guard
    // here too so a bypassed/stale disabled attribute can't fire a PATCH
    // that overwrites a correct stored value with an unintended one.
    if (fiscalYearEndMonth === null) return;
    const prev = fiscalYearEndMonth;
    setFiscalYearEndMonth(month);
    try {
      await apiPatch("/api/firm-settings/", { fiscal_year_end_month: month });
    } catch (e) {
      console.error(e);
      setFiscalYearEndMonth(prev);
      setErr("Failed to save the fiscal year end");
    }
  };

  const INVOICE_FONT_MIN = 9;
  const INVOICE_FONT_MAX = 18;

  /** Saved on blur, not on every keystroke: "1" on the way to "11" is a valid
   *  number and would otherwise be written and then overwritten. */
  const saveInvoiceFontSize = async () => {
    const value = Number(invoiceFontSize);
    if (!Number.isInteger(value) || value < INVOICE_FONT_MIN || value > INVOICE_FONT_MAX) {
      setFontSizeError(`Invoice text size must be between ${INVOICE_FONT_MIN} and ${INVOICE_FONT_MAX}.`);
      return;
    }
    setFontSizeError("");
    try {
      await apiPatch("/api/firm-settings/", { invoice_font_size: value });
    } catch (e) {
      console.error(e);
      setFontSizeError("Failed to save the invoice text size.");
    }
  };

  // Django escapes the constraint name's quotes into entities, which turns the
  // most useful part of the message into noise. Decode the handful that Django
  // actually emits; this is an error message, not a page being rendered.
  const decodeEntities = (text) =>
    text
      .replace(/&quot;/g, '"')
      .replace(/&#x27;|&#39;/g, "'")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&");

  // A rejected add carries the reason in the DRF error body -- a per-field
  // dict ({username: ["..."]}) or a bare detail. Show it: "Failed to add
  // employee" alone is why a duplicate username reads as the app refusing to
  // add employees at all.
  const addEmployeeError = (e) => {
    const data = e?.response?.data;
    // A 500 from a DEBUG-enabled server answers with Django's technical error
    // page. api.js hands that over as {message: "<!DOCTYPE html>..."}, and
    // reading it out loud buries the one line that says what broke.
    const html = typeof data?.message === "string" && /^\s*<(!doctype|html)/i.test(data.message)
      ? data.message
      : null;
    if (html) {
      const exception = html.match(/<pre class="exception_value">([\s\S]*?)<\/pre>/i);
      const title = html.match(/<title>([\s\S]*?)<\/title>/i);
      const text = decodeEntities((exception?.[1] || title?.[1] || "").trim());
      return text
        ? `Failed to add employee\n\n${text}`
        : "Failed to add employee";
    }
    const reasons = [];
    if (data && typeof data === "object" && !Array.isArray(data)) {
      Object.entries(data).forEach(([field, val]) => {
        const text = Array.isArray(val) ? val.join(" ") : String(val ?? "");
        if (!text) return;
        // A field-keyed message already names its field for anything the form
        // shows; `detail` and `non_field_errors` are the message itself.
        reasons.push(
          field === "detail" || field === "non_field_errors" ? text : `${field}: ${text}`
        );
      });
    }
    // A 500 has no usable field detail -- say the add failed rather than
    // parroting "Server Error" at the admin.
    const usable = reasons.filter((r) => !/^detail: /.test(r) && !/server error/i.test(r));
    return usable.length
      ? `Failed to add employee\n\n${usable.join("\n")}`
      : "Failed to add employee";
  };

  // ===== Add Employee =====
  const addEmployee = async () => {
    if (!newEmployee.first_name || !newEmployee.username) {
      alert("First name and username are required");
      return;
    }
    const payload = {
      ...newEmployee,
      password: "ChangeMe123!",
      rates: ratesMapToList(newRates), // <-- list for API
    };
    try {
      await apiPost("/employees/", payload);
      alert("Employee added successfully");
      setNewEmployee({
        first_name: "",
        last_name: "",
        username: "",
        email: "",
        role: "STAFF",
        is_active: true,
        can_approve_time: false,
        can_finalize_invoices: false,
      });
      setNewRates({});
      loadAll();
    } catch (e) {
      console.error(e);
      alert(addEmployeeError(e));
    }
  };

  // ===== Save Employee (names, username, email, role, status, rates) =====
  const saveEmployee = async (emp) => {
    const payload = {
      // Flat fields are accepted by your serializer and merged into user fields
      username: emp.username || "",
      email: emp.email || "",
      first_name: emp.first_name || "",
      last_name: emp.last_name || "",
      role: emp.role || "STAFF",
      is_active: !!emp.is_active,
      can_approve_time: !!emp.can_approve_time,
      can_finalize_invoices: !!emp.can_finalize_invoices,
      auto_approve_own_time: !!emp.auto_approve_own_time,
      // Blank means no target, not zero -- coerce to null rather than "".
      // The backend is a PositiveIntegerField and 400s on a negative value;
      // HTML5 `min` only constrains the spinner, not typed input, so clamp
      // here too rather than let the user hit a server error for it.
      monthly_capacity_hours:
        emp.monthly_capacity_hours === "" || emp.monthly_capacity_hours == null
          ? null
          : Math.max(0, Number(emp.monthly_capacity_hours)),
      // "" is not a date and Django answers 400; null is what clears a field.
      hire_date: emp.hire_date ? emp.hire_date : null,
      vacation_weeks:
        emp.vacation_weeks === "" || emp.vacation_weeks == null
          ? null
          : Number(emp.vacation_weeks),
      // Convert map -> list for API
      rates: ratesMapToList(emp.rates),
    };

    try {
      await apiPatch(`/employees/${emp.id}/`, payload);
      setEditingEmployeeId(null);
      await loadAll();
    } catch (e) {
      console.error("Save failed", e);
      alert("Failed to save employee");
    }
  };

  // ===== QuickBooks disconnect =====
  const disconnectQbo = async () => {
    try {
      await apiPost("/api/quickbooks/disconnect/", {});
      setQbo((prev) => (prev ? { ...prev, connected: false, needs_reconnect: false } : prev));
    } catch (e) {
      console.error(e);
      alert("Failed to disconnect QuickBooks");
    }
  };

  // ===== Change Password via correct endpoint =====
  const changePassword = async (empId) => {
    const pw = prompt("Enter new password:");
    if (!pw) return;
    try {
      // Correct endpoint is the action /set_password/
      await apiPost(`/employees/${empId}/set_password/`, { password: pw });
      alert("Password changed");
    } catch (e) {
      console.error(e);
      alert("Failed to change password");
    }
  };

  // ===== Render =====
  return (
    <div style={{ padding: "30px 40px", maxWidth: 1600, margin: "0 auto", background: "#f9fafb", minHeight: "100vh" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, color: "#111827" }}>Settings / Admin</h1>
        {err && <div style={{ color: "#dc2626", fontSize: 14, marginTop: 8 }}>{err}</div>}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {[
          { key: "general", label: "General" },
          { key: "holidays", label: "Holidays" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 16px",
              border: "none",
              borderBottom: activeTab === tab.key ? "2px solid #111827" : "2px solid transparent",
              background: "transparent",
              fontWeight: 600,
              fontSize: 14,
              color: activeTab === tab.key ? "#111827" : "#6b7280",
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "holidays" && <HolidayCalendar />}

      {activeTab === "general" && (
      <>
      {/* Every section is collapsible and starts closed; which ones are open
          is remembered per browser (CollapsibleSection's storageKey). */}

      {/* ===== Firm ===== */}
      <CollapsibleSection title="Firm" storageKey="settings.firm">
          <label htmlFor="fiscal-year-end-month" style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
            Fiscal year end
          </label>
          <select
            id="fiscal-year-end-month"
            value={fiscalYearEndMonth ?? ""}
            disabled={fiscalYearEndMonth === null}
            onChange={(e) => saveFiscalYearEndMonth(Number(e.target.value))}
            style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, background: "white" }}
          >
            {fiscalYearEndMonth === null && (
              <option value="">{err ? "Unavailable" : "Loading…"}</option>
            )}
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </select>

          <p style={{ fontSize: 13, color: "#6b7280", marginTop: 12, marginBottom: 0 }}>
            The month the practice's fiscal year ends. June means the fiscal year runs 1 July – 30 June.
          </p>

          <label htmlFor="invoice-font-size" style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginTop: 24, marginBottom: 6 }}>
            Invoice text size
          </label>
          <input
            id="invoice-font-size"
            type="number"
            min={INVOICE_FONT_MIN}
            max={INVOICE_FONT_MAX}
            value={invoiceFontSize}
            onChange={(e) => { setInvoiceFontSize(e.target.value); setFontSizeError(""); }}
            onBlur={saveInvoiceFontSize}
            style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, background: "white", width: 110 }}
          />
          {fontSizeError && (
            <p style={{ fontSize: 13, color: "#b91c1c", marginTop: 8, marginBottom: 0 }}>{fontSizeError}</p>
          )}
          <p style={{ fontSize: 13, color: "#6b7280", marginTop: 12, marginBottom: 0 }}>
            Applies to every invoice, so they all look alike. 13 is the size they
            were designed at; smaller fits more on a page.
          </p>
      </CollapsibleSection>

      {/* ===== Add Employee Form ===== */}
      <CollapsibleSection title="Add Employee" storageKey="settings.add-employee">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
            <input
              type="text"
              placeholder="First name"
              value={newEmployee.first_name}
              onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })}
              style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14 }}
            />
            <input
              type="text"
              placeholder="Last name"
              value={newEmployee.last_name}
              onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })}
              style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14 }}
            />
            <input
              type="text"
              placeholder="Username"
              value={newEmployee.username}
              onChange={(e) => setNewEmployee({ ...newEmployee, username: e.target.value })}
              style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14 }}
            />
            <input
              type="email"
              placeholder="Email"
              value={newEmployee.email}
              onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
              style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14 }}
            />
            <select
              value={newEmployee.role}
              onChange={(e) => setNewEmployee({ ...newEmployee, role: e.target.value })}
              style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, background: "white" }}
            >
              <option value="ADMIN">Admin</option>
              <option value="STAFF">Staff</option>
              <option value="MANAGER">Manager</option>
            </select>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!newEmployee.can_approve_time}
              onChange={(e) => setNewEmployee({ ...newEmployee, can_approve_time: e.target.checked })}
            />
            <span style={{ fontWeight: 600 }}>Can approve time</span>
            <span style={{ color: "#6b7280" }}>— gives access to the Time Approval console</span>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!newEmployee.can_finalize_invoices}
              onChange={(e) => setNewEmployee({ ...newEmployee, can_finalize_invoices: e.target.checked })}
            />
            <span style={{ fontWeight: 600 }}>Can finalise invoices</span>
            <span style={{ color: "#6b7280" }}>— may take an invoice out of Draft, which sends it to QuickBooks</span>
          </label>

          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
            <span style={{ fontWeight: 600 }}>Default Password:</span> ChangeMe123!
          </p>

          {/* Dynamic Hourly Rates */}
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: "#374151" }}>Initial Hourly Rates</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 12 }}>
              {timeCodes.map((code) => (
                <div key={code.id}>
                  <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600, textTransform: "uppercase" }}>
                    {code.code}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newRates[code.code] || ""}
                    placeholder="0"
                    onChange={(e) => setNewRates({ ...newRates, [code.code]: e.target.value })}
                    style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%" }}
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={addEmployee}
            style={{ width: "100%", background: "#0F172A", color: "white", borderRadius: 8, padding: "12px 16px", border: "none", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          >
            Add Employee
          </button>
      </CollapsibleSection>

      {/* ===== Employee List ===== */}
      <CollapsibleSection title="Employees" storageKey="settings.employees">
          {employees.some((e) => !e.hire_date) && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a",
                          borderRadius: 8, padding: "10px 12px", marginBottom: 12,
                          fontSize: 13, color: "#92400e" }}>
              No hire date recorded for{" "}
              {employees.filter((e) => !e.hire_date)
                        .map((e) => displayName(e)).join(", ")}
              . Vacation accrual cannot be calculated without one.
            </div>
          )}

          {employees.map((emp) => {
            const isEditing = editingEmployeeId === emp.id;
            return (
              <div key={emp.id} style={{ borderTop: "1px solid #f3f4f6", paddingTop: 20, paddingBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <h3 style={{ fontWeight: 600, fontSize: 16, margin: 0, color: "#111827" }}>
                    {displayName(emp)}
                  </h3>
                  <div style={{ display: "flex", gap: 8 }}>
                    {isEditing ? (
                      <button
                        onClick={() => saveEmployee(emp)}
                        style={{ fontSize: 13, padding: "6px 16px", background: "#111827", color: "white", border: "1px solid #111827", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
                      >
                        Done (Save)
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingEmployeeId(emp.id)}
                          style={{ fontSize: 13, padding: "6px 16px", background: "white", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 500 }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => changePassword(emp.id)}
                          style={{ fontSize: 13, padding: "6px 16px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 500 }}
                        >
                          Change Password
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Editable fields */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>First Name</label>
                    <input
                      type="text"
                      value={emp.first_name || ""}
                      disabled={!isEditing}
                      onChange={(e) => setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, first_name: e.target.value } : p)))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    />
                  </div>
                  <div>
                    <label htmlFor={`hire-date-${emp.id}`}
                           style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                      Hire Date
                    </label>
                    <input
                      id={`hire-date-${emp.id}`}
                      type="date"
                      value={emp.hire_date || ""}
                      disabled={!isEditing}
                      onChange={(e) => setEmployees((prev) => prev.map((p) =>
                        (p.id === emp.id ? { ...p, hire_date: e.target.value } : p)))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Last Name</label>
                    <input
                      type="text"
                      value={emp.last_name || ""}
                      disabled={!isEditing}
                      onChange={(e) => setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, last_name: e.target.value } : p)))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Username</label>
                    <input
                      type="text"
                      value={emp.username || ""}
                      disabled={!isEditing}
                      onChange={(e) => setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, username: e.target.value } : p)))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Email</label>
                    <input
                      type="text"
                      value={emp.email || ""}
                      disabled={!isEditing}
                      onChange={(e) => setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, email: e.target.value } : p)))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Role</label>
                    <select
                      value={emp.role || "STAFF"}
                      disabled={!isEditing}
                      onChange={(e) => setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, role: e.target.value } : p)))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    >
                      <option value="ADMIN">Admin</option>
                      <option value="STAFF">Staff</option>
                      <option value="MANAGER">Manager</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Status</label>
                    <select
                      value={emp.is_active ? "active" : "inactive"}
                      disabled={!isEditing}
                      onChange={(e) => {
                        const next = e.target.value === "active";
                        setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, is_active: next } : p)));
                      }}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Time Approval</label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, background: isEditing ? "white" : "#f9fafb", cursor: isEditing ? "pointer" : "default" }}>
                      <input
                        type="checkbox"
                        aria-label={`Can approve time — ${emp.username || emp.full_name || emp.id}`}
                        checked={!!emp.can_approve_time}
                        disabled={!isEditing}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, can_approve_time: next } : p)));
                        }}
                      />
                      <span>Can approve time</span>
                    </label>
                    {/* Their own submitted time skips the queue -- for the
                        person who would otherwise approve it themselves. */}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginTop: 6, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, background: isEditing ? "white" : "#f9fafb", cursor: isEditing ? "pointer" : "default" }}>
                      <input
                        type="checkbox"
                        aria-label={`Auto-approve own time — ${emp.username || emp.full_name || emp.id}`}
                        checked={!!emp.auto_approve_own_time}
                        disabled={!isEditing}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, auto_approve_own_time: next } : p)));
                        }}
                      />
                      <span>Auto-approve own time</span>
                    </label>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Invoicing</label>
                    {/* Issuing numbers the invoice and puts it in front of a
                        client, and the same flag allows "Sync to QuickBooks",
                        so it is a per-person decision rather than a side
                        effect of role. */}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, background: isEditing ? "white" : "#f9fafb", cursor: isEditing ? "pointer" : "default" }}>
                      <input
                        type="checkbox"
                        aria-label={`Can finalise invoices — ${emp.username || emp.full_name || emp.id}`}
                        checked={!!emp.can_finalize_invoices}
                        disabled={!isEditing}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, can_finalize_invoices: next } : p)));
                        }}
                      />
                      <span>Can finalise invoices</span>
                    </label>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Monthly capacity (hours)</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={emp.monthly_capacity_hours ?? ""}
                      placeholder="No target"
                      disabled={!isEditing}
                      onChange={(e) => {
                        const next = e.target.value;
                        setEmployees((prev) => prev.map((p) => (p.id === emp.id ? { ...p, monthly_capacity_hours: next } : p)));
                      }}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    />
                    <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, marginBottom: 0 }}>
                      Leave blank for no target. Used to tint the planning grid; it never blocks anything.
                    </p>
                  </div>
                  <div>
                    <label htmlFor={`vacation-weeks-${emp.id}`}
                           style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                      Vacation Weeks
                    </label>
                    <input
                      id={`vacation-weeks-${emp.id}`}
                      type="number" min="0" max="52" step="0.5"
                      placeholder="Statutory"
                      value={emp.vacation_weeks ?? ""}
                      disabled={!isEditing}
                      onChange={(e) => setEmployees((prev) => prev.map((p) =>
                        (p.id === emp.id ? { ...p, vacation_weeks: e.target.value } : p)))}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                    />
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                      Blank = Saskatchewan minimum (3 weeks, 4 after 10 years)
                    </div>
                  </div>
                </div>

                {/* Editable Rates */}
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, marginTop: 20, color: "#374151" }}>
                    Hourly Rates
                  </h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 12 }}>
                    {timeCodes.map((code) => (
                      <div key={code.id}>
                        <label style={{ display: "block", fontSize: 11, color: "#6b7280", marginBottom: 4, fontWeight: 600, textTransform: "uppercase" }}>
                          {code.code}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={emp.rates?.[code.code] ?? ""}
                          placeholder="0"
                          disabled={!isEditing}
                          onChange={(e) =>
                            setEmployees((prev) =>
                              prev.map((p) =>
                                p.id === emp.id
                                  ? { ...p, rates: { ...(p.rates || {}), [code.code]: e.target.value } }
                                  : p
                              )
                            )
                          }
                          style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, width: "100%", background: isEditing ? "white" : "#f9fafb" }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
      </CollapsibleSection>

      {/* ===== Roll-forward ===== */}
      <CollapsibleSection
        title="Roll-forward"
        storageKey="settings.roll-forward"
        headerExtra={
          <a href="/settings/roll-forward" style={{ fontSize: 14, color: "#3730a3", fontWeight: 600 }}>
            Run a roll-forward
          </a>
        }
      >
          <p style={{ fontSize: 13, color: "#6b7280", marginTop: 0, marginBottom: 20 }}>
            These settings decide which tasks are ticked by default when you run a
            roll-forward. They never stop you rolling anything forward manually.
          </p>
          <RollForwardSettings />
      </CollapsibleSection>

      {/* ===== Invoice descriptions ===== */}
      <CollapsibleSection title="Invoice descriptions" storageKey="settings.invoice-descriptions">
        <InvoiceDescriptionsSection />
      </CollapsibleSection>

      {/* ===== Master files ===== */}
      <CollapsibleSection title="Master files" storageKey="settings.master-files">
        <MasterFileSection />
      </CollapsibleSection>

      {/* ===== QuickBooks Integration ===== */}
      <CollapsibleSection
        title="QuickBooks"
        storageKey="settings.quickbooks"
        headerExtra={
          qbo && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 12,
                  background: qbo.environment === "production" ? "#fde68a" : "#e0e7ff",
                  color: qbo.environment === "production" ? "#92400e" : "#3730a3",
                }}
              >
                {qbo.environment === "production" ? "LIVE" : "SANDBOX"}
              </span>
          )
        }
      >
          {qbo?.needs_reconnect && (
            <p style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>
              QuickBooks needs to be reconnected.
            </p>
          )}

          <p style={{ fontSize: 14, color: "#374151", marginBottom: 16 }}>
            Status:{" "}
            {qbo?.connected ? (
              <span style={{ fontWeight: 600, color: "#111827" }}>Connected (realm {qbo.realm_id})</span>
            ) : (
              <span style={{ fontWeight: 600, color: "#6b7280" }}>Not connected</span>
            )}
          </p>

          {!qbo?.connected || qbo?.needs_reconnect ? (
            <button
              onClick={async () => {
                try {
                  const data = await apiGet("/api/quickbooks/connect_url/");
                  if (data?.authorize_url) {
                    window.location.href = data.authorize_url;
                  } else {
                    alert("Could not start the QuickBooks connection.");
                  }
                } catch (e) {
                  alert("Could not start the QuickBooks connection.");
                }
              }}
              style={{ background: "#0F172A", color: "white", borderRadius: 8, padding: "10px 16px", border: "none", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
            >
              Connect QuickBooks
            </button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <a href="/settings/quickbooks/customers" style={{ fontSize: 14, color: "#3730a3", fontWeight: 600 }}>
                Manage customer mapping
              </a>
              <button
                onClick={disconnectQbo}
                style={{ fontSize: 13, padding: "8px 16px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 500 }}
              >
                Disconnect
              </button>
            </div>
          )}
      </CollapsibleSection>
      </>
      )}
    </div>
  );
}

// ===== Statutory holiday calendar =====
//
// Saskatchewan's ten public holidays are generated from the statutory rules
// for any year -- nothing to seed, no horizon to run out. This is where a
// manager sees any year's dates and corrects one when the firm observes
// something different (e.g. an office closure that moves a date).
function HolidayCalendar() {
  const [year, setYear] = useState(new Date().getFullYear());
  // A separate string for the input's own display. A controlled number
  // input whose value reverts to the last valid year on every keystroke
  // (e.g. via `Number(e.target.value) || year`) fights a clear-then-type
  // interaction: clearing produces "" -> falls back to the old year ->
  // React redraws the field back to that old year -> the next keystroke
  // types onto stale digits instead of a blank field. Tracking the raw
  // text here lets the field go through an empty/partial state honestly,
  // and `year` (what actually drives the fetch) only updates once the
  // text parses to a real number.
  const [yearInput, setYearInput] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [edits, setEdits] = useState({});

  const load = React.useCallback(async (y) => {
    setErr("");
    try {
      setData(await getStatHolidays(y));
      setEdits({});
    } catch (e) {
      setErr(e.message || "Could not load the calendar.");
    }
  }, []);

  useEffect(() => { load(year); }, [year, load]);

  // Matches the input's own min/max, and the range the API accepts. `year`
  // drives the fetch and this fires on every keystroke, so without the bounds
  // clearing the field and retyping "2030" asks for years 2, 20 and 203 on the
  // way -- each a real ten-holiday calendar for antiquity -- and typing one
  // digit too many asks for 20300, which is a 400.
  const YEAR_MIN = 1900;
  const YEAR_MAX = 2100;

  function handleYearChange(e) {
    const raw = e.target.value;
    setYearInput(raw);
    const n = Number(raw);
    if (raw !== "" && Number.isInteger(n) && n >= YEAR_MIN && n <= YEAR_MAX) {
      setYear(n);
    }
  }

  // Always sends the current date, even when only the note changed -- a POST
  // that omits `date` used to be indistinguishable from an explicit null,
  // and null means "not observed", which silently deleted a statutory
  // holiday and zeroed every employee's stat credit for that day.
  async function save(name) {
    const current = data.holidays.find((h) => h.name === name);
    try {
      await saveStatHolidayOverride({
        year, name,
        date: edits[name] === "" ? null : (edits[name] ?? current?.date ?? null),
        note: edits[`${name}__note`] ?? current?.note ?? "",
      });
      await load(year);
    } catch (e) { setErr(e.message); }
  }

  async function revert(name) {
    try {
      await revertStatHoliday(year, name);
      await load(year);
    } catch (e) { setErr(e.message); }
  }

  // Deliberately NOT `if (err) return`: a failed save, or one 400 from a
  // half-typed year, used to replace the whole calendar with an error string
  // and throw away what you were looking at. The error goes above the table
  // instead. Nothing has loaded yet is still a bare message -- there is no
  // table to keep.
  if (!data) {
    return err
      ? <div style={{ color: "#b91c1c" }}>{err}</div>
      : <div>Loading…</div>;
  }

  return (
    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, marginTop: 0, marginBottom: 12, color: "#111827" }}>
        Holidays
      </h2>
      {err && (
        <div role="alert"
             style={{ background: "#fef2f2", border: "1px solid #fecaca",
                      color: "#b91c1c", borderRadius: 8, padding: "10px 12px",
                      marginBottom: 12, fontSize: 13 }}>
          {err}
        </div>
      )}
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        Saskatchewan's 10 public holidays, calculated from the statutory rules —
        every year is already covered, including years far in the future.
        Override a row only where the firm observes something different.
      </div>

      <label htmlFor="holiday-year" style={{ fontWeight: 600, fontSize: 13 }}>
        Year{" "}
        <input id="holiday-year" type="number" min={YEAR_MIN} max={YEAR_MAX}
               value={yearInput}
               onChange={handleYearChange}
               style={{ padding: "6px 10px", borderRadius: 6,
                        border: "1px solid #e5e7eb", width: 100 }} />
      </label>

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16,
                      fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
            <th style={{ padding: "8px" }}>Holiday</th>
            <th style={{ padding: "8px" }}>Date</th>
            <th style={{ padding: "8px" }}>Day</th>
            <th style={{ padding: "8px" }}>Note</th>
            {data.can_edit && <th style={{ padding: "8px" }} />}
          </tr>
        </thead>
        <tbody>
          {data.holidays.map((h) => (
            <tr key={h.name} style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "8px", fontWeight: 600 }}>
                {h.name}
                {h.observed_for && (
                  <span style={{ fontWeight: 400, color: "#6b7280" }}>
                    {" "}(observed for {h.observed_for})
                  </span>
                )}
              </td>
              {/* A suppressed holiday (an override with a null date) has no
                  date and no weekday. It is listed anyway, flagged, because
                  clearing the date input is one keystroke and save() maps ""
                  to null -- the ordinary "blank this" gesture IS the suppress
                  gesture. Hidden, the row could only be brought back by a
                  superuser deleting it in /admin/; Revert below is the way
                  back. No date input on this row: there is nothing to edit
                  until it is reverted. */}
              {h.suppressed ? (
                <td style={{ padding: "8px", color: "#b45309", fontWeight: 600 }}
                    colSpan={2}>
                  Not observed
                </td>
              ) : (
                <>
                  <td style={{ padding: "8px" }}>
                    {data.can_edit ? (
                      <input
                        type="date"
                        aria-label={`Date for ${h.name}`}
                        value={edits[h.name] ?? h.date}
                        onChange={(e) =>
                          setEdits((p) => ({ ...p, [h.name]: e.target.value }))}
                      />
                    ) : h.date}
                  </td>
                  <td style={{ padding: "8px", color: "#6b7280" }}>{h.weekday}</td>
                </>
              )}
              <td style={{ padding: "8px" }}>
                {h.source === "override" ? (
                  <span style={{ color: "#92400e" }}>
                    {h.note || "Overridden"}
                  </span>
                ) : (
                  <span style={{ color: "#9ca3af" }}>From the rules</span>
                )}
              </td>
              {data.can_edit && (
                <td style={{ padding: "8px" }}>
                  {!h.suppressed && (
                    <button onClick={() => save(h.name)}
                            aria-label={`Save ${h.name}`}>Save</button>
                  )}
                  {h.source === "override" && (
                    <button onClick={() => revert(h.name)}
                            aria-label={`Revert ${h.name}`}
                            style={{ marginLeft: h.suppressed ? 0 : 8 }}>
                      Revert
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
